import { Dispatch, useReducer } from 'react';
import { endReducer } from '@/utils/end-reducer';
import { moveItem } from '@/experiences/sales-portal/domain/order-guides/blocs/utils/array-utils';
import {
  OrderGuide,
  OrderGuideItem,
  SelectedOrderGuideItem,
} from '@/experiences/sales-portal/domain/order-guides/blocs/types';

export enum EditOrderGuideActionType {
  ADD_ITEM = 'ADD_ITEM',
  CREATE_PARTITION = 'CREATE_PARTITION',
  DELETE_ITEMS = 'DELETE_ITEMS',
  HYDRATE = 'HYDRATE',
  REORDER_ITEMS = 'REORDER_ITEMS',
  SELECT_ITEM = 'SELECT_ITEM',
  SET_ITEM_PAR_LEVEL = 'SET_ITEM_PAR_LEVEL',
  SET_PARTITION_SELECTION = 'SET_PARTITION_SELECTION',
}

// Append incoming catalog packs to a partition, joining same-item groups and
// skipping packs already present.
interface AddItemAction {
  items: OrderGuideItem[];
  partitionUUID: string;
  type: EditOrderGuideActionType.ADD_ITEM;
}

// Appends a new empty partition. `partitionUUID` is a client-generated,
// session-local id — the BE recreates non-default partitions under fresh
// server uuids on every save, so this id is solely used on the frontend for
// identity management.
interface CreatePartitionAction {
  payload: { name: string; partitionUUID: string };
  type: EditOrderGuideActionType.CREATE_PARTITION;
}
// Removes the selected/provided packs,
// Items may span partitions in a single dispatch.
interface DeleteItemsAction {
  payload: { items: SelectedOrderGuideItem[] };
  type: EditOrderGuideActionType.DELETE_ITEMS;
}

// Splices one grouped row from `fromIndex` to `toIndex` within the partition.
interface ReorderItemsAction {
  payload: {
    fromIndex: number;
    partitionUUID: string;
    toIndex: number;
  };
  type: EditOrderGuideActionType.REORDER_ITEMS;
}

// A selectable pack within a partition. `itemUUID` rides along for the
// distinct-item count; (partitionUUID, variantPackUUID) is the unique key.
type SelectablePack = Omit<SelectedOrderGuideItem, 'partitionUUID'>;

interface SelectItemAction {
  payload: SelectedOrderGuideItem;
  type: EditOrderGuideActionType.SELECT_ITEM;
}

interface SetPartitionSelectionAction {
  payload: { packs: SelectablePack[]; partitionUUID: string };
  type: EditOrderGuideActionType.SET_PARTITION_SELECTION;
}

type GuideAction =
  | AddItemAction
  | CreatePartitionAction
  | DeleteItemsAction
  | {
      payload: { hydratedState: OrderGuide };
      type: EditOrderGuideActionType.HYDRATE;
    }
  | ReorderItemsAction
  | {
      payload: {
        parLevel: number | null;
        partitionUUID: string;
        variantPackUUID: string;
      };
      type: EditOrderGuideActionType.SET_ITEM_PAR_LEVEL;
    };

export type EditOrderGuideAction =
  | GuideAction
  | SelectItemAction
  | SetPartitionSelectionAction;

export type EditOrderGuideDispatch = Dispatch<EditOrderGuideAction>;

export interface EditOrderGuideReducerState {
  guide: OrderGuide;
  selectedItems: SelectedOrderGuideItem[];
}

// (partition uuid + variant pack uuid) is the best way to target packs/items since the same item can exist in multiple partitions/groups
const packKey = (partitionUUID: string, variantPackUUID: string): string =>
  `${partitionUUID}:${variantPackUUID}`;

// Address the entry by (partition, variant pack) — the only pair unique per
// row once items are grouped. Returns the same ref when nothing changed.
const setItemParLevel = (
  state: OrderGuide,
  {
    parLevel,
    partitionUUID,
    variantPackUUID,
  }: {
    parLevel: number | null;
    partitionUUID: string;
    variantPackUUID: string;
  },
): OrderGuide => {
  let didChange = false;

  const partitions = state.partitions.map((partition) => {
    if (partition.partitionUUID !== partitionUUID) {
      return partition;
    }

    const itemGroups = partition.itemGroups.map((group) => {
      const index = group.items.findIndex(
        (item) => item.variantPackUUID === variantPackUUID,
      );
      if (index === -1 || group.items[index].parLevel === parLevel) {
        return group;
      }

      didChange = true;
      const items = [...group.items];
      items[index] = { ...items[index], parLevel };
      return { ...group, items };
    });

    return didChange ? { ...partition, itemGroups } : partition;
  });

  return didChange ? { ...state, partitions } : state;
};

// Splice one grouped row from `fromIndex` to `toIndex` in the addressed
// partition. Returns the same ref when nothing moves or the partition is gone.
const reorderItems = (
  state: OrderGuide,
  { fromIndex, partitionUUID, toIndex }: ReorderItemsAction['payload'],
): OrderGuide => {
  if (fromIndex === toIndex) {
    return state;
  }

  const partitionIndex = state.partitions.findIndex(
    (partition) => partition.partitionUUID === partitionUUID,
  );
  if (partitionIndex === -1) {
    return state;
  }

  const partition = state.partitions[partitionIndex];
  const lastIndex = partition.itemGroups.length - 1;
  if (
    fromIndex < 0 ||
    fromIndex > lastIndex ||
    toIndex < 0 ||
    toIndex > lastIndex
  ) {
    return state;
  }

  const itemGroups = moveItem(partition.itemGroups, fromIndex, toIndex);

  const partitions = [...state.partitions];
  partitions[partitionIndex] = { ...partition, itemGroups };
  return { ...state, partitions };
};

// Append incoming packs to one partition, joining same-item groups and
// skipping packs already present. Returns the same ref when nothing new lands.
const addItems = (
  state: OrderGuide,
  { items, partitionUUID }: Omit<AddItemAction, 'type'>,
): OrderGuide => {
  const partitionIndex = state.partitions.findIndex(
    (partition) => partition.partitionUUID === partitionUUID,
  );
  if (partitionIndex === -1) {
    return state;
  }

  const partition = state.partitions[partitionIndex];

  const seenPackUUIDs = new Set(
    partition.itemGroups.flatMap((group) =>
      group.items.map((item) => item.variantPackUUID),
    ),
  );
  const newItems = items.filter((item) => {
    if (seenPackUUIDs.has(item.variantPackUUID)) {
      return false;
    }
    seenPackUUIDs.add(item.variantPackUUID);
    return true;
  });
  if (newItems.length === 0) {
    return state;
  }

  const itemGroups = [...partition.itemGroups];
  const groupIndexByItemUUID = new Map(
    itemGroups.map((group, index) => [group.itemUUID, index]),
  );

  for (const item of newItems) {
    const groupIndex = groupIndexByItemUUID.get(item.itemUUID);
    if (groupIndex === undefined) {
      groupIndexByItemUUID.set(item.itemUUID, itemGroups.length);
      itemGroups.push({ items: [item], itemUUID: item.itemUUID });
    } else {
      const group = itemGroups[groupIndex];
      itemGroups[groupIndex] = { ...group, items: [...group.items, item] };
    }
  }

  const partitions = [...state.partitions];
  partitions[partitionIndex] = { ...partition, itemGroups };
  return { ...state, partitions };
};

// Append a new empty partition. Array position is the persisted order — the
// follow-up autosave derives sort order from it.
const createPartition = (
  state: OrderGuide,
  { name, partitionUUID }: CreatePartitionAction['payload'],
): OrderGuide => ({
  ...state,
  partitions: [
    ...state.partitions,
    { isDefault: false, itemGroups: [], name, partitionUUID },
  ],
});
// Remove the provided/selected packs from their partitions, Returns the same ref when nothing matched.
const deleteItems = (
  state: OrderGuide,
  items: SelectedOrderGuideItem[],
): OrderGuide => {
  if (items.length === 0) {
    return state;
  }

  const keysToDelete = new Set(
    items.map((item) => packKey(item.partitionUUID, item.variantPackUUID)),
  );
  let didChange = false;

  const partitions = state.partitions.map((partition) => {
    let partitionChanged = false;
    const itemGroups = partition.itemGroups
      .map((group) => {
        const items = group.items.filter(
          (item) =>
            !keysToDelete.has(
              packKey(partition.partitionUUID, item.variantPackUUID),
            ),
        );
        if (items.length === group.items.length) {
          return group;
        }
        partitionChanged = true;
        return { ...group, items };
      })
      .filter((group) => group.items.length > 0);

    if (!partitionChanged) {
      return partition;
    }
    didChange = true;
    return { ...partition, itemGroups };
  });

  return didChange ? { ...state, partitions } : state;
};

export const editOrderGuideReducer = (
  state: OrderGuide,
  action: GuideAction,
): OrderGuide => {
  switch (action.type) {
    case EditOrderGuideActionType.ADD_ITEM:
      return addItems(state, action);
    case EditOrderGuideActionType.CREATE_PARTITION:
      return createPartition(state, action.payload);
    case EditOrderGuideActionType.DELETE_ITEMS:
      return deleteItems(state, action.payload.items);
    case EditOrderGuideActionType.HYDRATE:
      return action.payload.hydratedState;
    case EditOrderGuideActionType.REORDER_ITEMS:
      return reorderItems(state, action.payload);
    case EditOrderGuideActionType.SET_ITEM_PAR_LEVEL:
      return setItemParLevel(state, action.payload);
    default:
      return endReducer(state, action);
  }
};

// Toggle one (partition, variant pack) in the selection set.
const toggleSelectedItem = (
  selectedItems: SelectedOrderGuideItem[],
  { itemUUID, partitionUUID, variantPackUUID }: SelectItemAction['payload'],
): SelectedOrderGuideItem[] => {
  const index = selectedItems.findIndex(
    (selected) =>
      selected.partitionUUID === partitionUUID &&
      selected.variantPackUUID === variantPackUUID,
  );
  if (index === -1) {
    return [...selectedItems, { itemUUID, partitionUUID, variantPackUUID }];
  }
  return selectedItems.filter((_, i) => i !== index);
};

// Replace the selection for one partition, leaving other partitions intact.
const setPartitionSelection = (
  selectedItems: SelectedOrderGuideItem[],
  { packs, partitionUUID }: SetPartitionSelectionAction['payload'],
): SelectedOrderGuideItem[] => {
  const others = selectedItems.filter(
    (selected) => selected.partitionUUID !== partitionUUID,
  );
  return [...others, ...packs.map((pack) => ({ ...pack, partitionUUID }))];
};

export const editOrderGuideStateReducer = (
  state: EditOrderGuideReducerState,
  action: EditOrderGuideAction,
): EditOrderGuideReducerState => {
  switch (action.type) {
    case EditOrderGuideActionType.SELECT_ITEM:
      return {
        ...state,
        selectedItems: toggleSelectedItem(state.selectedItems, action.payload),
      };
    case EditOrderGuideActionType.SET_PARTITION_SELECTION:
      return {
        ...state,
        selectedItems: setPartitionSelection(
          state.selectedItems,
          action.payload,
        ),
      };
    case EditOrderGuideActionType.HYDRATE:
      return { guide: action.payload.hydratedState, selectedItems: [] };
    case EditOrderGuideActionType.DELETE_ITEMS: {
      const guide = editOrderGuideReducer(state.guide, action);
      if (guide === state.guide) {
        return state;
      }
      const deletedKeys = new Set(
        action.payload.items.map((item) =>
          packKey(item.partitionUUID, item.variantPackUUID),
        ),
      );
      return {
        guide,
        selectedItems: state.selectedItems.filter(
          (selected) =>
            !deletedKeys.has(
              packKey(selected.partitionUUID, selected.variantPackUUID),
            ),
        ),
      };
    }
    default: {
      const guide = editOrderGuideReducer(state.guide, action);
      return guide === state.guide ? state : { ...state, guide };
    }
  }
};

/**
 * Holds the editor's working copy of the guide and selection state; exposes a
 * dispatch for par-level edits, row reordering, hydration, and item selection.
 *
 * @param initialState - Committed guide used to seed the reducer.
 */
export const useEditOrderGuideReducer = (
  initialState: OrderGuide,
): [EditOrderGuideReducerState, EditOrderGuideDispatch] =>
  useReducer(editOrderGuideStateReducer, initialState, (guide) => ({
    guide,
    selectedItems: [],
  }));
